import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@material-ui/core/Dialog';
import FormController from '../../../components/globals/FormController';
import { heading2 } from '../../../styled/mixins';
import { Button as BaseButton } from '../../../components/buttons';
import GlobalTheme from '../../../styled/GlobalTheme';
import { SECONDARY } from '../../../components/buttons/constants';
import UserProvider from '../../../services/entities/UserProvider';
import { AlertsContext } from '../../../contexts/AlertsContext';
import { AnalyticsContext } from '../../../contexts/AnalyticsContext';

const Form = styled.div`
  max-width: 434px;
  height: 436px;
  padding: 25px 28px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${GlobalTheme.breakpoints.down('xs')}{
    padding: 18px 23px;
  }
`;

const Title = styled.h2`
  ${heading2}
  @media (max-width: 400px) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  @media (max-width: 400px) {
    font-size: 14px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 400px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const Button = styled(BaseButton)`
  && {
    &:nth-child(1) {
      margin-right: 12px;
    }
    @media (max-width: 400px) {
      width: 100%;
      &:nth-child(1) {
        order: 1;
        margin-right: 0;
      }
      margin-bottom: 14px;
    }
  }
`;
const Help = styled.div`
  margin-top: 40px;
  @media (max-width: 400px) {
    margin-top: 16px;
  }
`;
const HelpDescription = styled.p`
  margin: 0;
  font-size: 14px;
  text-align: center;
`;
const SupportEmail = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
`;
const FormDialog = ({ open, setOpen }) => {
  const [email, setEmail] = useState();
  const { showAlert, showError } = useContext(AlertsContext);
  const { dataLayerPush } = useContext(AnalyticsContext);
  const intl = useIntl();
  const handleClose = () => {
    setOpen(false);
  };
  const handleInputChange = (e) => {
    const { value } = e.target;
    setEmail(value);
  };
  const sendEmail = async () => {
    const response = await UserProvider.resetPassword(email);
    const { success } = response;
    if (success) {
      showAlert(
        intl.formatMessage({
          id: 'login.resetPassword.succes.alert',
          defaultMessage: 'The reset password email was sent.',
        }),
      );
    } else {
      showError(intl.formatMessage({
        id: 'alert.error.recoverPassword',
        defaultMessage: "We couldn't send your recover email. Please try again or contact support@socialpiper.com.",
      }));
    }
    setOpen(false);
  };
  const handleEnter = () => {
    dataLayerPush({
      pagePath: '/login/forgot-password',
      pathTitle: 'Login - Forgot password',
      event: 'pv',
    });
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onEntered={handleEnter}
      aria-labelledby="form-dialog-reset-password"
    >
      <Form>
        <Title>
          <FormattedMessage
            id="login.resetPassword"
            defaultMessage="Reset password"
          />
        </Title>
        <Description>
          <FormattedMessage
            id="login.resetPassword.description"
            defaultMessage="Please write the email you registered in Social Piper. You'll receive a link to create a new password."
          />
        </Description>
        <FormController
          id="useremail"
          onChange={handleInputChange}
          label="E-mail"
          value={email}
        />
        <ButtonsContainer>
          <Button
            type={SECONDARY}
            handlerClick={() => setOpen(false)}
          >
            <FormattedMessage
              id="k.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button
            handlerClick={sendEmail}
          >
            <FormattedMessage
              id="k.send.email"
              defaultMessage="Send email"
            />
          </Button>
        </ButtonsContainer>
        <Help>
          <HelpDescription>
            <FormattedMessage
              id="login.resetPassword.helpSupport"
              defaultMessage="If you encounter any problem, please contact us at  <a>support@socialpiper.com</a> or use our live chat."
              values={{
                a: (...chunks) => (
                  <SupportEmail href="mailto:support@socialpiper.com">
                    {chunks}
                  </SupportEmail>
                ),
              }}
            />
          </HelpDescription>
        </Help>
      </Form>
    </Dialog>
  );
};

FormDialog.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default FormDialog;
